import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getCategorySEO } from '../utils/helpers/seo';
// Critical above-the-fold components
import { Breadcrumb } from '../components/common';
import { HeaderSection, SEOWrapper, FooterSection } from '../components/layout';
import {  GeneralSection, FleetSection, ServicesSection, TestimonialsSection, HowItWorksSection, FeaturesSection, CTASection, TrustedBySection, FAQSection, LocationsSection, HeroSectionWithForm } from '../components/sections';
import { usePageData, useBreadcrumbs } from '../utils/hooks';

import { fetchCategoryPage, fetchConfigPage } from '../utils/api/api';
import { Loader } from '../components/common';
import Custom404 from './404';

export default function CategoryPage({ initialData,notFound=false, error }) {
    const router = useRouter();
    const [strapiData, setStrapiData] = useState(initialData);
    const [isLoading, setIsLoading] = useState(!initialData);
    const [apiError, setError] = useState(error);

    // Extract all data using custom hook
    const {
        configData,
        pageData,
        header,
        footer,
        heroSection,
        generalContent,
        fleetData,
        trustedByData,
        servicesData,
        testimonialsData,
        howItWorksData,
        featuresData,
        ctaData,
        locationsData
    } = usePageData(strapiData);

    // Generate breadcrumbs using custom hook
    const breadcrumbs = useBreadcrumbs('category', pageData, router.query);

    // Get FAQ data with fallback to default - handle multiple spellings
    const faqSource = pageData?.FAQ || pageData?.faq || pageData?.Faqs || pageData?.faqs || pageData?.FAQs;
    const faqData = {
        order: faqSource?.order || faqSource?.Order || 12,
        title: faqSource?.title || faqSource?.headline || 'FAQs',
        description: faqSource?.description || '',
        faqs: faqSource?.faqs || [],
    };

    // Create sections array with order - handle nested arrays
    const createOrderedSections = () => {
        const allSections = [];
        
        // Process GeneralSection - extract individual items
        if (generalContent) {
            const generalItems = Array.isArray(generalContent) ? generalContent : [generalContent];
            generalItems.forEach((item, index) => {
                allSections.push({
                    component: <GeneralSection data={[item]} globalIndex={index} />,
                    order: item.order || item.Order || 1 + (index * 0.1) // Add small offset for same order
                });
            });
        }
        
        // Add other sections
        if (trustedByData) {
            allSections.push({
                component: <TrustedBySection data={trustedByData} />,
                order: trustedByData.order || trustedByData.Order || 2
            });
        }
        
        if (featuresData) {
            allSections.push({
                component: <FeaturesSection data={featuresData} />,
                order: featuresData.order || featuresData.Order || 3
            });
        }
        
        if (fleetData) {
            allSections.push({
                component: <FleetSection fleetData={fleetData} />,
                order: fleetData.order || fleetData.Order || 4
            });
        }
        
        if (locationsData) {
            allSections.push({
                component: <LocationsSection data={locationsData} />,
                order: locationsData.order || locationsData.Order || 4.5
            });
        }
        
        if (servicesData) {
            allSections.push({
                component: <ServicesSection data={servicesData} />,
                order: servicesData.order || servicesData.Order || 5
            });
        }
        
        if (howItWorksData) {
            allSections.push({
                component: <HowItWorksSection data={howItWorksData} />,
                order: howItWorksData.order || howItWorksData.Order || 6
            });
        }
        
        if (testimonialsData) {
            allSections.push({
                component: <TestimonialsSection data={testimonialsData} />,
                order: testimonialsData.order || testimonialsData.Order || 7
            });
        }
        
        if (faqData) {
            allSections.push({
                component: <FAQSection data={faqData} />,
                order: faqData.order || faqData.Order || 12
            });
        }
        
        if (ctaData) {
            allSections.push({
                component: <CTASection data={ctaData} />,
                order: ctaData.order || ctaData.Order || 13
            });
        }
        
        // Sort by order
        return allSections.sort((a, b) => a.order - b.order);
    };

    const sections = createOrderedSections();


    // Loading fallback component
    const SectionLoader = () => <Loader type="section" size="medium" />;

    // Update data when route changes using initial data
    useEffect(() => {
        if (initialData && initialData.pageUrl !== strapiData?.pageUrl) {
            setStrapiData(initialData);
            setError(null);
        }
    }, [initialData, strapiData?.pageUrl]);


    if (notFound) {
        return <Custom404 />;
    }

    if (isLoading) {
        return <Loader type="page" message="Loading..." fullPage={true} />;
    }

    if (apiError) {
        return <Loader type="error" message="Error loading content" errorMessage={apiError} fullPage={true} />;
    }

    // Generate SEO data for this category page
    const seoData = getCategorySEO(pageData, configData, strapiData.pageUrl);


    return (
        <>
            <SEOWrapper 
                pageData={pageData}
                configData={configData}
                pageUrl={strapiData.pageUrl}
                seoData={seoData}
            />

            {/* Critical above-the-fold components */}
            <HeaderSection data={header} pageUrl={strapiData.pageUrl} phoneNumber={pageData.EnhancedHeroSection?.phone} />
            <HeroSectionWithForm data={heroSection} phoneNumber={header?.Contact} />
            <Breadcrumb breadcrumbs={breadcrumbs} />

            <div className="page-container" key={router.asPath}>
                <main className="main-content" style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* All sections rendered in order */}
                    {sections.map((section, index) => (
                        <div key={index} style={{ order: section.order }}>
                            {section.component}
                        </div>
                    ))}
                </main>

                {/* Footer Section */}
                <FooterSection data={footer} />
            </div>
        </>
    );
}

// Server-side data fetching
export async function getServerSideProps({ params, req, res }) {
    try {
        const { categorySlug } = params;
        
        
        // Parallelize API calls to reduce server response time
        const [pageData, configData] = await Promise.all([
            fetchCategoryPage(categorySlug, true), // true = server-side
            fetchConfigPage(true) // true = server-side
        ]);

        if (
            !pageData.data || // covers null/undefined
            typeof pageData.data !== 'object' || // covers non-objects
            (Array.isArray(pageData.data) && pageData.data.every(item => item == null)) // covers [null], [undefined]
          ) {
            return {
              props: {
                initialData: null,
                notFound: true,
                error: null,
              },
            };
          }

      
        return {
            props: {
                initialData: {
                    configData: configData,
                    pageData: pageData.data[0],
                    pageUrl: `/${categorySlug}`,
                },
                notFound: false,
                categorySlug: categorySlug,
                error: null,
            },
        };
    } catch (err) {
        console.error('Error fetching data from Strapi:', err);
        
        // If it's an authorization error, return 401
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
            return {
                redirect: {
                    destination: '/500',
                    permanent: false,
                },
            };
        }
        
        return {
            props: {
                initialData: null,
                error: err.message,
            },
        };
    }
}

